import { parseTimestampsAsBigInt } from "@/shared/json";
import { LoggerService } from "@/shared/services";

type DatasetLinkContext = {
  dataset: {
    dataset_id: string;
    t?: bigint;
  };
};

type FileLinkContext = {
  file: {
    file_id: string;
    t?: bigint;
  };
};

type TopicLinkContext = {
  topic: {
    topic_id: string;
    t?: bigint;
  };
};

type MessagePathLinkContext = {
  message_path: {
    message_path_id: string;
    t?: bigint;
  };
};

const isDatasetLinkContext = (json: unknown): json is DatasetLinkContext => {
  return (
    typeof json === "object" &&
    json !== null &&
    "dataset" in json &&
    typeof json.dataset === "object" &&
    json.dataset !== null &&
    "dataset_id" in json.dataset &&
    typeof json.dataset.dataset_id === "string" &&
    (!("t" in json.dataset) || typeof json.dataset.t === "bigint")
  );
};

const isFileLinkContext = (json: unknown): json is FileLinkContext => {
  return (
    typeof json === "object" &&
    json !== null &&
    "file" in json &&
    typeof json.file === "object" &&
    json.file !== null &&
    "file_id" in json.file &&
    typeof json.file.file_id === "string" &&
    (!("t" in json.file) || typeof json.file.t === "bigint")
  );
};

const isTopicLinkContext = (json: unknown): json is TopicLinkContext => {
  return (
    typeof json === "object" &&
    json !== null &&
    "topic" in json &&
    typeof json.topic === "object" &&
    json.topic !== null &&
    "topic_id" in json.topic &&
    typeof json.topic.topic_id === "string" &&
    (!("t" in json.topic) || typeof json.topic.t === "bigint")
  );
};

const isMessagePathLinkContext = (
  json: unknown,
): json is MessagePathLinkContext => {
  return (
    typeof json === "object" &&
    json !== null &&
    "message_path" in json &&
    typeof json.message_path === "object" &&
    json.message_path !== null &&
    "message_path_id" in json.message_path &&
    typeof json.message_path.message_path_id === "string" &&
    (!("t" in json.message_path) || typeof json.message_path.t === "bigint")
  );
};

export const isTimestampLink = (url: string) => {
  return url.startsWith("timestamp:");
};

export type RobotoUriType =
  | "collection"
  | "dataset"
  | "device"
  | "event"
  | "file"
  | "invocation"
  | "layout"
  | "msgpath"
  | "org"
  | "session"
  | "topic"
  | "trigger"
  | "workspace";

/**
 * The `<type>` values this app knows how to open. Narrower than the set of
 * entities the AI can fetch — the prompt says which types it may link, but
 * nothing on the wire enforces that, so anything else must parse as "not a
 * link" rather than be trusted.
 *
 * `ui` is reserved and must never be added here: it namespaces non-entity
 * links that address the app's own controls (see AgentThread's
 * `roboto://ui/time-format`), which rely on parsing as "not an entity".
 */
const ROBOTO_URI_TYPES: ReadonlySet<string> = new Set<RobotoUriType>([
  "collection",
  "dataset",
  "device",
  "event",
  "file",
  "invocation",
  "layout",
  "msgpath",
  "org",
  "session",
  "topic",
  "trigger",
  "workspace",
]);

export function isRobotoUriType(value: string): value is RobotoUriType {
  return ROBOTO_URI_TYPES.has(value);
}

export interface ParsedRobotoUri {
  type: RobotoUriType;
  id: string;
  t?: bigint;
}

/**
 * Parse a roboto:// URI and extract the resource type, ID, and optional timestamp.
 * Supports formats like:
 * - roboto://dataset/ds_123
 * - roboto://dataset/ds_123?t=1740099243000000000
 * - roboto://file/fl_123?t=0
 * - roboto://topic/tp_123
 * - roboto://msgpath/mp_123?t=1740099243000000000
 * - roboto://collection/cl_123
 * - roboto://workspace/ws_123?t=1740099243000000000
 * - roboto://layout/ly_123
 */
export function parseRobotoUri(url: string): ParsedRobotoUri | undefined {
  try {
    if (!url.startsWith("roboto://")) {
      return undefined;
    }

    const urlObj = new URL(url);

    // In roboto://dataset/ds_123, "dataset" is the hostname and "/ds_123" is the pathname
    const type = urlObj.hostname;
    const pathParts = urlObj.pathname.split("/").filter((p) => p.length > 0);

    // We expect exactly one path part (the ID)
    if (pathParts.length !== 1) {
      LoggerService.warn(
        `Expected 1 path part in roboto:// URI, got ${pathParts.length}: ${urlObj.pathname}`,
      );
    }

    const id = pathParts[0];

    // `roboto://collection/` names a type but no entity. Treating it as
    // unparseable keeps every consumer from having to re-check that `id` is
    // really there before building a route out of it.
    if (id === undefined) {
      return undefined;
    }

    if (!isRobotoUriType(type)) {
      return undefined;
    }

    const tParam = urlObj.searchParams.get("t");
    const t = tParam ? BigInt(tParam) : undefined;

    return { type, id, t };
  } catch {
    return undefined;
  }
}

export const isRobotoUri = (url: string) => {
  return url.startsWith("roboto://");
};

/**
 * A legacy link href is a JSON object blob, either raw or percent-encoded.
 *
 * Cheap prefix test so everything else — every `roboto://` URI, every external
 * URL — is rejected without the decode-and-parse below. That path costs a
 * *thrown exception per miss*, and the four `is*Link` predicates built on it
 * run for every link in every markdown render pass (i.e. per streamed chunk),
 * so the misses vastly outnumber the hits.
 */
const JSON_BLOB_HREF = /^\s*(?:\{|%7[Bb])/;

function getLinkContext<T>(
  url: string,
  isLink: (json: unknown) => json is T,
): T | undefined {
  if (!JSON_BLOB_HREF.test(url)) {
    return undefined;
  }
  try {
    const decoded = decodeURIComponent(url);
    const json: unknown = JSON.parse(decoded, parseTimestampsAsBigInt);
    return isLink(json) ? json : undefined;
  } catch {
    return undefined;
  }
}

export const getTopicLinkContext = (
  url: string,
): TopicLinkContext | undefined => {
  // Try roboto:// URI format first
  const robotoUri = parseRobotoUri(url);
  if (robotoUri && robotoUri.type === "topic") {
    return {
      topic: {
        topic_id: robotoUri.id,
        t: robotoUri.t,
      },
    };
  }

  // Fall back to legacy JSON format
  return getLinkContext(url, isTopicLinkContext);
};

export const isTopicLink = (url: string) => {
  return getTopicLinkContext(url) !== undefined;
};

export const getMessagePathLinkContext = (
  url: string,
): MessagePathLinkContext | undefined => {
  // Try roboto:// URI format first
  const robotoUri = parseRobotoUri(url);
  if (robotoUri && robotoUri.type === "msgpath") {
    return {
      message_path: {
        message_path_id: robotoUri.id,
        t: robotoUri.t,
      },
    };
  }

  // Fall back to legacy JSON format
  return getLinkContext(url, isMessagePathLinkContext);
};

export const isMessagePathLink = (url: string) => {
  return getMessagePathLinkContext(url) !== undefined;
};

export const getDatasetLinkContext = (
  url: string,
): DatasetLinkContext | undefined => {
  // Try roboto:// URI format first
  const robotoUri = parseRobotoUri(url);
  if (robotoUri && robotoUri.type === "dataset") {
    return {
      dataset: {
        dataset_id: robotoUri.id,
        t: robotoUri.t,
      },
    };
  }

  // Fall back to legacy JSON format
  return getLinkContext(url, isDatasetLinkContext);
};

export const isDatasetLink = (url: string) => {
  return getDatasetLinkContext(url) !== undefined;
};

export const getFileLinkContext = (
  url: string,
): FileLinkContext | undefined => {
  // Try roboto:// URI format first
  const robotoUri = parseRobotoUri(url);
  if (robotoUri && robotoUri.type === "file") {
    return {
      file: {
        file_id: robotoUri.id,
        t: robotoUri.t,
      },
    };
  }

  // Fall back to legacy JSON format
  return getLinkContext(url, isFileLinkContext);
};

export const isFileLink = (url: string) => {
  return getFileLinkContext(url) !== undefined;
};
